import React, { useEffect, useState } from 'react';
import { NavermapsProvider } from 'react-naver-maps';
import NaverMap from './components/NaverMap';
import Schedule from './components/Schedule';
import ScheduleSettings from './components/ScheduleSettings';
import AuthModal from './components/AuthModal';
import VehicleForm from './components/VehicleForm';
import StudentList from './components/StudentList';
import { supabase } from './lib/supabase';
import { LogIn, LogOut, Car, Settings } from 'lucide-react';
import { DndContext, DragOverlay, closestCenter, DragEndEvent, useSensors, useSensor, MouseSensor, TouchSensor, PointerSensor } from '@dnd-kit/core';
import { arrayMove, SortableContext } from '@dnd-kit/sortable';
import type { Student, Vehicle, ScheduleViewSettings } from './types';

function App() {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showVehicleForm, setShowVehicleForm] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [draggedStudent, setDraggedStudent] = useState<Student | null>(null);
  const [scheduleSettings, setScheduleSettings] = useState<ScheduleViewSettings>({
    startHour: 8,
    endHour: 22,
    visibleDays: [0, 1, 2, 3, 4, 5, 6],
    view: 'week'
  });
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [schedulingInfo, setSchedulingInfo] = useState<{
    student: Student;
    date: string;
    hour: number;
  } | null>(null);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUser(user);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (user) {
      loadVehicles();
      loadStudents();
    }
  }, [user]);

  const loadVehicles = async () => {
    const { data, error } = await supabase
      .from('vehicles')
      .select('*')
      .eq('user_id', user.id);

    if (error) {
      console.error('Error loading vehicles:', error);
      return;
    }

    setVehicles(data);
  };

  const loadStudents = async () => {
    const { data, error } = await supabase
      .from('students')
      .select('*')
      .eq('user_id', user.id);

    if (error) {
      console.error('Error loading students:', error);
      return;
    }

    setStudents(data);
  };

  const handleSaveVehicle = (vehicle: Vehicle) => {
    setVehicles(prev => [...prev, vehicle]);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setDraggedStudent(null);
    
    if (!over || !active.data.current?.student) return;

    const student = active.data.current.student as Student;
    const [date, hour] = over.id.toString().split('-');
    
    setSchedulingInfo({
      student,
      date,
      hour: parseInt(hour)
    });
    setShowScheduleModal(true);
  };

  const handleScheduleConfirm = async (vehicleId: string, type: 'pickup' | 'dropoff') => {
    if (!schedulingInfo) return;
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // 같은 날짜에 같은 유형(승차/하차)의 일정이 있는지 확인
      const { data: existingSchedules, error: fetchError } = await supabase
        .from('schedules')
        .select('*')
        .eq('student_id', schedulingInfo.student.id)
        .eq('date', schedulingInfo.date)
        .eq('type', type);

      if (fetchError) throw fetchError;

      if (existingSchedules && existingSchedules.length > 0) {
        alert(`이미 해당 날짜에 ${type === 'pickup' ? '승차' : '하차'} 일정이 있습니다.`);
        setShowScheduleModal(false);
        setSchedulingInfo(null);
        return;
      }

      const { error } = await supabase
        .from('schedules')
        .insert([{
          student_id: schedulingInfo.student.id,
          date: schedulingInfo.date,
          start_time: `${schedulingInfo.hour}:00`,
          end_time: `${schedulingInfo.hour + 1}:00`,
          user_id: user.id,
          vehicle_id: vehicleId,
          type: type
        }]);

      if (error) throw error;
      
      await loadSchedules();
      setShowScheduleModal(false);
      setSchedulingInfo(null);
    } catch (error) {
      console.error('Error saving schedule:', error);
      alert('스케줄 저장 중 오류가 발생했습니다.');
    }
  };

  return (
    <NavermapsProvider 
      ncpClientId={import.meta.env.VITE_NAVER_CLIENT_ID}
    >
      <div className="min-h-screen bg-gray-100">
        <header className="bg-white shadow-sm p-4">
          <div className="container mx-auto flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-800">학원 차량 자동 배차 시스템</h1>
            <div className="flex items-center gap-4">
              {user ? (
                <>
                  <button
                    onClick={() => setShowVehicleForm(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Car size={20} />
                    차량 관리
                  </button>
                  <button
                    onClick={() => setShowSettingsModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <Settings size={20} />
                    스케줄 설정
                  </button>
                  <button
                    onClick={() => supabase.auth.signOut()}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <LogOut size={20} />
                    로그아웃
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setShowAuthModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <LogIn size={20} />
                  로그인
                </button>
              )}
            </div>
          </div>
        </header>

        <main className="container mx-auto p-4">
          <NaverMap />
          <div className="mt-4">
            <DndContext 
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={(event) => {
                const student = event.active.data.current?.student as Student;
                if (student) {
                  setDraggedStudent(student);
                }
              }}
              onDragEnd={handleDragEnd}
            >
              <Schedule
                students={students}
                vehicles={vehicles}
                settings={scheduleSettings}
                onSettingsChange={setScheduleSettings}
                draggedStudent={draggedStudent}
                onDragEnd={() => setDraggedStudent(null)}
              />
              <div className="mt-4">
                <StudentList
                  students={students}
                  onDragStart={setDraggedStudent}
                />
              </div>
            </DndContext>
          </div>
        </main>

        {showAuthModal && (
          <AuthModal
            onClose={() => setShowAuthModal(false)}
            onSuccess={() => setShowAuthModal(false)}
          />
        )}

        {showVehicleForm && (
          <VehicleForm
            vehicles={vehicles}
            onClose={() => setShowVehicleForm(false)}
            onSave={handleSaveVehicle}
            onUpdate={loadVehicles}
          />
        )}

        {showSettingsModal && (
          <ScheduleSettings
            settings={scheduleSettings}
            onSettingsChange={setScheduleSettings}
            onClose={() => setShowSettingsModal(false)}
          />
        )}
      </div>
    </NavermapsProvider>
  );
}

export default App;